import { PARTY_ROLE, PARTY_ROLE_DISPLAY, PARTY_STATUS, type PartyRoleId } from '@steerfi/shared';
import type React from 'react';
import { PartnerMetadataEditor } from '@/components/partners/partner-metadata-editor';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface PartnerFormState {
  partner_name: string;
  partner_code: string;
  party_role_id: number | '';
  party_status_id: number;
  metadata: string;
}

export const emptyPartnerForm = (): PartnerFormState => ({
  partner_name: '',
  partner_code: '',
  party_role_id: '',
  party_status_id: PARTY_STATUS.ACTIVE,
  metadata: '',
});

interface PartnerFormProps {
  idPrefix: string;
  values: PartnerFormState;
  onChange: <K extends keyof PartnerFormState>(key: K, value: PartnerFormState[K]) => void;
  fieldErrors: Partial<Record<string, string>>;
  metadataError?: string | undefined;
  /** Renders the destructive code-change warning callout (edit flow). */
  showCodeWarning?: boolean;
}

export const PartnerForm: React.FC<PartnerFormProps> = ({
  idPrefix,
  values,
  onChange,
  fieldErrors,
  metadataError,
  showCodeWarning = false,
}) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-partner-name`}>
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-partner-name`}
          placeholder="e.g. Galileo Financial Technologies"
          autoFocus
          value={values.partner_name}
          onChange={(e) => onChange('partner_name', e.target.value)}
          aria-invalid={!!fieldErrors.partner_name}
        />
        {fieldErrors.partner_name && (
          <p className="text-sm text-destructive">{fieldErrors.partner_name}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-partner-code`}>
          Code <span className="text-destructive">*</span>
        </Label>
        {showCodeWarning && (
          <Alert variant="destructive">
            <AlertTitle as="h3">Changing the partner code is risky</AlertTitle>
            <AlertDescription className="text-xs">
              Changing the partner code may break references in seeds, integrations, party
              relationships, and external systems. Only update if you understand the impact.
            </AlertDescription>
          </Alert>
        )}
        <Input
          id={`${idPrefix}-partner-code`}
          placeholder="GALILEO"
          value={values.partner_code}
          onChange={(e) => onChange('partner_code', e.target.value.toUpperCase())}
          aria-invalid={!!fieldErrors.partner_code}
        />
        {fieldErrors.partner_code && (
          <p className="text-sm text-destructive">{fieldErrors.partner_code}</p>
        )}
        <p className="text-xs text-muted-foreground">
          3–10 uppercase letters and numbers (e.g. GALILEO)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>
            Party role <span className="text-destructive">*</span>
          </Label>
          <Select
            onValueChange={(v) => onChange('party_role_id', Number(v))}
            value={values.party_role_id === '' ? '' : String(values.party_role_id)}
          >
            <SelectTrigger aria-invalid={!!fieldErrors.party_role_id}>
              <SelectValue placeholder="Select party role" />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(PARTY_ROLE) as [string, number][]).map(([, value]) => (
                <SelectItem key={value} value={String(value)}>
                  {PARTY_ROLE_DISPLAY[value as PartyRoleId] ?? String(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldErrors.party_role_id && (
            <p className="text-sm text-destructive">{fieldErrors.party_role_id}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select
            onValueChange={(v) => onChange('party_status_id', Number(v))}
            value={String(values.party_status_id)}
          >
            <SelectTrigger aria-invalid={!!fieldErrors.party_status_id}>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {/* Only Active/Inactive: the list filter and status colours model
                  those two, and the ticket specifies a default of Active. */}
              <SelectItem value={String(PARTY_STATUS.ACTIVE)}>Active</SelectItem>
              <SelectItem value={String(PARTY_STATUS.INACTIVE)}>Inactive</SelectItem>
            </SelectContent>
          </Select>
          {fieldErrors.party_status_id && (
            <p className="text-sm text-destructive">{fieldErrors.party_status_id}</p>
          )}
        </div>
      </div>

      <PartnerMetadataEditor
        id={`${idPrefix}-partner-metadata`}
        value={values.metadata}
        onChange={(v) => onChange('metadata', v)}
        error={metadataError}
      />
    </div>
  );
};
